import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const { 
  FiSmartphone, FiShield, FiClock, FiRefreshCw, FiLock, FiMail, 
  FiActivity, FiInfo, FiLayers, FiCheckCircle, FiExternalLink, FiSettings
} = FiIcons;
import { mockApps, mockTemplates } from '../data/mockData';

const OTPSettings = () => {
  const [selectedAppId, setSelectedAppId] = useState('app_1');
  const [config, setConfig] = useState({
    mode: 'External Apps Script',
    otpLength: 6,
    expiryMinutes: 5,
    resendCooldown: 60,
    maxResends: 3,
    maxAttempts: 5,
    lockoutMinutes: 30,
    templateId: 'tpl_2',
    channel: 'email',
    fallbackEnabled: true
  });

  const handleSave = () => {
    alert('OTP configuration updated for ' + mockApps.find(a => a.id === selectedAppId).name);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-700">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">OTP Settings</h2>
          <p className="text-sm text-slate-500">Global and app-specific One-Time Password policies.</p>
        </div>
        <div className="flex gap-3">
          <select 
            className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-medium outline-none focus:ring-2 focus:ring-blue-100 shadow-sm"
            value={selectedAppId}
            onChange={(e) => setSelectedAppId(e.target.value)}
          >
            {mockApps.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <button onClick={handleSave} className="px-6 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 transition-all shadow-md flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiSave} /> Save Configuration
          </button>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-4 shadow-sm">
        <SafeIcon icon={FiInfo} className="text-blue-500 text-xl shrink-0 mt-0.5" />
        <div className="space-y-1">
          <p className="text-xs text-blue-800 leading-relaxed font-medium">
            <b>Current Mode:</b> Generation and verification are handled by your <b>External Apps Script</b>. BPA Email Gateway only handles the delivery of the OTP email. BPA Managed OTP mode will be available in v1.2.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Mode Selector */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden p-6">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <SafeIcon icon={FiLayers} className="text-blue-500" /> OTP Operation Mode
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <button 
                onClick={() => setConfig({...config, mode: 'External Apps Script'})}
                className={`p-4 rounded-xl border-2 text-left transition-all ${config.mode === 'External Apps Script' ? 'border-blue-500 bg-blue-50' : 'border-slate-100 hover:border-slate-200'}`}
              >
                <p className="font-bold text-sm text-slate-800">External Apps Script</p>
                <p className="text-[10px] text-slate-500 mt-1">Gateway acts as a carrier only. Best for legacy integrations.</p>
              </button>
              <button 
                disabled
                className="p-4 rounded-xl border-2 border-slate-50 bg-slate-50 text-left opacity-60 cursor-not-allowed"
              >
                <div className="flex justify-between">
                  <p className="font-bold text-sm text-slate-400">BPA Managed</p>
                  <span className="text-[8px] bg-slate-200 px-1.5 py-0.5 rounded text-slate-500 uppercase font-bold">Coming Soon</span>
                </div>
                <p className="text-[10px] text-slate-400 mt-1">Full lifecycle management: Gen, Store, Verify & Audit.</p>
              </button>
            </div>
          </div>

          {/* Security Policy */}
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center gap-2">
              <SafeIcon icon={FiShield} className="text-slate-400" />
              <h3 className="font-bold text-slate-800">Verification Policy</h3>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">OTP Length</label>
                <div className="flex gap-2">
                  {[4, 6, 8].map(l => (
                    <button key={l} onClick={() => setConfig({...config, otpLength: l})} className={`flex-1 py-2 text-xs font-bold rounded-lg transition-all ${config.otpLength === l ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>{l} Digits</button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Expiry Window</label>
                <div className="relative">
                  <input type="number" className="w-full pl-4 pr-12 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm" value={config.expiryMinutes} onChange={(e) => setConfig({...config, expiryMinutes: e.target.value})} />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-bold text-slate-400">MINS</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Resend Cooldown</label>
                <div className="relative">
                  <input type="number" className="w-full pl-4 pr-12 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm" value={config.resendCooldown} onChange={(e) => setConfig({...config, resendCooldown: e.target.value})} />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-bold text-slate-400">SECS</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Max Verification Attempts</label>
                <input type="number" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm" value={config.maxAttempts} onChange={(e) => setConfig({...config, maxAttempts: e.target.value})} />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-slate-900 rounded-2xl p-6 text-white space-y-6">
            <h4 className="font-bold text-sm flex items-center gap-2">
              <SafeIcon icon={FiSettings} className="text-blue-400" /> Delivery Config
            </h4>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Channel</label>
                <div className="flex gap-2">
                  <button className="flex-1 py-2 bg-blue-600 text-white text-[10px] font-bold rounded-lg flex items-center justify-center gap-1.5"><SafeIcon icon={FiMail} /> Email</button>
                  <button disabled className="flex-1 py-2 bg-slate-800 text-slate-500 text-[10px] font-bold rounded-lg flex items-center justify-center gap-1.5 opacity-50"><SafeIcon icon={FiSmartphone} /> SMS</button>
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest">OTP Template</label>
                <select className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-xs outline-none" value={config.templateId} onChange={(e) => setConfig({...config, templateId: e.target.value})}>
                  {mockTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-3 cursor-pointer group pt-2">
                <div className={`w-10 h-5 rounded-full p-1 transition-all ${config.fallbackEnabled ? 'bg-emerald-500' : 'bg-slate-700'}`} onClick={() => setConfig({...config, fallbackEnabled: !config.fallbackEnabled})}>
                  <div className={`w-3 h-3 bg-white rounded-full transition-all transform ${config.fallbackEnabled ? 'translate-x-5' : 'translate-x-0'}`}></div>
                </div>
                <span className="text-[10px] font-bold text-slate-400 group-hover:text-white">Provider Fallback</span>
              </label>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
            <h4 className="font-bold text-slate-800 text-sm flex items-center gap-2">
              <SafeIcon icon={FiActivity} className="text-slate-400" /> Verification Logs
            </h4>
            <div className="py-8 text-center space-y-2">
              <div className="w-10 h-10 bg-slate-50 rounded-full flex items-center justify-center mx-auto text-slate-300">
                <SafeIcon icon={FiClock} />
              </div>
              <p className="text-[10px] text-slate-400 italic">Logs are handled by Apps Script in current mode.</p>
              <button disabled className="text-[10px] text-blue-600 font-bold hover:underline opacity-50">View External Logs</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTPSettings;